import { useState, useEffect, useMemo, useCallback } from 'react';
import { jornadaService } from '../services/JornadaService';
import { minutajeService } from '../services/MinutajeService';
import EstadisticasService  from '../services/EstadisticasService';
import { JornadaDto, JugadorMinutaje, RegistroJornadaResponseDto } from '../types/jornada';
import { JugadorDto } from '../types/jugador';

/**
 * Hook para gestionar jornadas y minutaje
 * Ahora recibe jugadores y equipo como parámetros para evitar duplicación
 */
export const useJornadas = (jugadoresEquipo: JugadorDto[], equipo: any) => {
  const [jornadas, setJornadas] = useState<JornadaDto[]>([]);
  const [jornadaSeleccionada, setJornadaSeleccionada] = useState<JornadaDto | null>(null);
  const [jugadoresMinutaje, setJugadoresMinutaje] = useState<JugadorMinutaje[]>([]);
  const [registrosExistentes, setRegistrosExistentes] = useState<RegistroJornadaResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [equipoRivalInput, setEquipoRivalInput] = useState<string>('');
  const [actualizandoRival, setActualizandoRival] = useState(false);

  /**
   * Selecciona una jornada y prepara los datos para el minutaje
   */
  const seleccionarJornada = async (jornada: JornadaDto, jugadoresEquipo: JugadorDto[]) => {
    if (!jugadoresEquipo || jugadoresEquipo.length === 0) {
      return;
    }
  
    setLoading(true);
    setError(null);
    
    setJornadaSeleccionada(jornada);
    setEquipoRivalInput(jornada.equipoRival || '');
  
    try {
      const registros = await minutajeService.obtenerRegistrosPorJornada(jornada.id);
      setRegistrosExistentes(registros);

      // Preparar jugadores con minutaje
      const jugadoresConMinutaje: JugadorMinutaje[] = jugadoresEquipo.map(jugador => {
        const registro = registros.find(r => r.jugadorId === jugador.id);
        return {
          id: jugador.id,
          nombre: jugador.nombre,
          apellido: jugador.apellido,
          posicion: jugador.posicion,
          seleccionado: !!registro,
          minutos: registro?.minutos || 0,
          titular: registro?.titular || false
        };
      });

      setJugadoresMinutaje(jugadoresConMinutaje);
    } catch (err) {
      setError('Error al cargar los datos de la jornada');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Actualiza el equipo rival de la jornada
   */
  const actualizarEquipoRival = async (equipoRival: string): Promise<boolean> => {
    if (!jornadaSeleccionada) {
      setError('No hay jornada seleccionada');
      return false;
    }

    setActualizandoRival(true);
    setError(null);

    try {
      const jornadaActualizada = await jornadaService.actualizarEquipoRival(jornadaSeleccionada.id, equipoRival);
      setJornadaSeleccionada(jornadaActualizada);
      setEquipoRivalInput(equipoRival);
      
      return true;
    } catch (err) {
      setError('Error al actualizar el equipo rival');
      return false;
    } finally {
      setActualizandoRival(false);
    }
  };

  /**
   * Actualiza los datos de un jugador en el minutaje
   */
  const actualizarJugadorMinutaje = (jugadorId: number, cambios: Partial<JugadorMinutaje>) => {
    setJugadoresMinutaje(prev => 
      prev.map(jugador => 
        jugador.id === jugadorId 
          ? { ...jugador, ...cambios }
          : jugador
      )
    );
  };

  /**
   * Valida que no haya más de 7 titulares (o 11 si es F11)
   */
  const validarTitulares = (jugadorId: number, nuevoEstadoTitular: boolean): boolean => {
    const titularesActuales = jugadoresMinutaje.filter(j => j.titular && j.id !== jugadorId).length;
    
    // Determinar límite según la sección del equipo (F7 o F11)
    // Si no hay sección definida, asumimos 7 por defecto para seguridad
    const esFutbol11 = equipo?.seccion?.includes('F11');
    const maxTitulares = esFutbol11 ? 11 : 7;
    
    if (nuevoEstadoTitular && titularesActuales >= maxTitulares) {
      setError(`No se pueden seleccionar más de ${maxTitulares} titulares`);
      return false;
    }
    
    setError(null);
    return true;
  };

  /**
   * Guarda los registros de minutaje
   */
  /**
   * Guarda el minutaje de los jugadores seleccionados
   */
  const guardarMinutaje = async (): Promise<boolean> => {
    
    if (!jornadaSeleccionada) {
      setError('No hay jornada seleccionada');
      return false;
    }
  
    const jugadoresSeleccionados = jugadoresMinutaje.filter(j => j.seleccionado);
  
    if (jugadoresSeleccionados.length === 0) {
      setError('Debe seleccionar al menos un jugador');
      return false;
    }
    
    // Validar minutos según la categoría del equipo
    const maxMinutos = EstadisticasService.obtenerDuracionPartido(equipo?.nombre || '');
    
    const jugadoresConMinutosInvalidos = jugadoresSeleccionados.filter(j => j.minutos < 1 || j.minutos > maxMinutos);
    if (jugadoresConMinutosInvalidos.length > 0) {
      setError(`Los minutos deben estar entre 1 y ${maxMinutos}`);
      return false;
    }
    
    setGuardando(true);
    setError(null);
    
    try {
      const resultados: RegistroJornadaResponseDto[] = [];
      
      // Procesar cada jugador seleccionado
      for (const jugador of jugadoresSeleccionados) {
        const registroExistente = registrosExistentes.find(r => r.jugadorId === jugador.id);
        
        if (registroExistente) {
          // Actualizar registro existente
          const registroActualizado = await minutajeService.actualizarRegistro(registroExistente.id, {
            minutos: jugador.minutos,
            titular: jugador.titular
          });
          resultados.push(registroActualizado);
        } else {
          // Crear nuevo registro
          const nuevoRegistro = await minutajeService.crearRegistro({
            jugadorId: jugador.id,
            jornadaId: jornadaSeleccionada.id,
            minutos: jugador.minutos,
            titular: jugador.titular
          });
          resultados.push(nuevoRegistro);
        }
      }
      
      
      // Recargar los datos después de guardar
      await seleccionarJornada(jornadaSeleccionada, jugadoresEquipo);
      
      return true;
    } catch (err) {
      setError('Error al guardar el minutaje');
      return false;
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Resetea el estado del modal
   */
  const resetearModal = () => {
    setJornadaSeleccionada(null);
    setJugadoresMinutaje([]);
    setRegistrosExistentes([]);
    setError(null);
  };

  /**
   * Memoizar el ID del equipo para evitar re-renders innecesarios
   */
  const equipoId = useMemo(() => equipo?.id, [equipo?.id]);

  /**
   * Carga las jornadas del equipo activo (memoizada)
   */
  const cargarJornadas = useCallback(async () => {
    if (!equipoId) {
      return;
    }

    setLoading(true);
    setError(null);
  
    try {
      const jornadas = await jornadaService.obtenerJornadasPorEquipo(equipoId);
      setJornadas(jornadas);
    } catch (err) {
      setError('Error al cargar las jornadas');
    } finally {
      setLoading(false);
    }
  }, [equipoId]); // ELIMINAR 'loading' de aquí

  // Cargar jornadas al montar el componente
  useEffect(() => {
    cargarJornadas();
  }, [cargarJornadas]); // Mantener la función memoizada como dependencia

  return {
    // Estado
    jornadas,
    jornadaSeleccionada,
    jugadoresMinutaje,
    registrosExistentes,
    jugadoresEquipo,
    equipoRivalInput,
    actualizandoRival,
    equipo,
    loading,
    error,
    guardando,
    // Acciones
    cargarJornadas,
    seleccionarJornada,
    actualizarJugadorMinutaje,
    validarTitulares,
    guardarMinutaje,
    setEquipoRivalInput,
    actualizarEquipoRival,
    resetearModal
  };
};